'use client'

import { useEffect, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { ArrowLeft, ArrowRight, Medal, PackageX, ShoppingBag, Trash2 } from 'lucide-react'
import { useCart, useCartRecommendations } from '@/lib/commerce/hooks'
import { formatPrice, isCustomerAuthActive, getCheckoutPath } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { toast } from '@/lib/toast'
import CartCard from './CartCard'
import RewardSelector from './RewardSelector'
import ProductCard from '../product/ProductCard'

function ProgressRow({ label, pct, barColor }: { label: React.ReactNode, pct: number, barColor: string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11.5px] gap-3">
        <div className="min-w-0 truncate">{label}</div>
        <span className="tabular-nums shrink-0" style={{ color: 'rgba(255,255,255,0.55)' }}>{Math.min(100, Math.round(pct))}%</span>
      </div>
      <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
      </div>
    </div>
  )
}

export default function Cart({ vendor, wishlist }: { vendor: any, wishlist?: any }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  const {
    cart, initialLoading, isValidating, canCheckout, validateCart,
    freeShippingProgress, amount, sale, salePriceSavings, activeOrderDiscount, orderDiscountAmount,
    appliedCoupon, cartTotal, discountBreakdown, couponDiscount, cartCount, clearCart, loading,
    loyaltyDiscountAmount, loyaltySaleOffsetAmount,
  } = useCart()
  const [hasMounted, setHasMounted] = useState(false)
  const searchParams = useSearchParams()

  const products = cart?.products || []
  const isLoggedIn = !!session?.user?.id
  const checkoutHref = navigateUrl(getCheckoutPath(vendor, isLoggedIn), pathname)
  const { recommendedProducts, isLoading: isLoadingRecommendations } = useCartRecommendations(vendor, products, 5)

  useEffect(() => { setHasMounted(true) }, [])

  useEffect(() => {
    if (searchParams.has('error')) toast.error('Бүртгэл олдсонгүй')
  }, [searchParams])

  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  if (!hasMounted) return null

  if (initialLoading) {
    return (
      <div className="cont py-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_380px]">
          <div className="space-y-4">
            <div className="h-8 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="h-32 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="h-32 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
          </div>
          <div className="h-72 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
        </div>
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="cont flex flex-col items-center justify-center py-20">
        <div
          className="w-24 h-24 rounded-full grid place-items-center mb-6"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <ShoppingBag className="w-10 h-10" style={{ color: 'rgba(255,255,255,0.4)' }} strokeWidth={1.5} />
        </div>
        <div className="text-[20px] sm:text-[24px] font-semibold text-white text-center" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
          Таны сагс хоосон байна
        </div>
        <div className="mt-2 text-[13.5px] text-center" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Та яг одоо сагсандаа бараа нэмээрэй!
        </div>
        <a
          href={navigateUrl('/product', pathname)}
          className="mt-7 inline-flex items-center gap-2 h-12 px-6 rounded-full text-[13.5px] font-semibold transition-opacity hover:opacity-90"
          style={{ background: 'white', color: '#0a0a0c' }}
        >
          <ShoppingBag className="h-4 w-4" /> Дэлгүүр хэсэх
        </a>
      </div>
    )
  }

  const validCoupon = couponDiscount > 0 && appliedCoupon && couponDiscount <= cartTotal ? couponDiscount : 0
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - (orderDiscountAmount || 0) - validCoupon)) : 0
  const saleSavingsRow = Math.max(0, (salePriceSavings || 0) - loyaltySaleOffsetAmount)
  const subtotal = Math.max(0, cartTotal - (orderDiscountAmount || 0) - validCoupon - loyaltyDiscount)
  const hasOutOfStock = products.some((p: any) => p.quantity === 0 || p.isOutOfStock)

  return (
    <div>
      <div className="cont pt-10 pb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Худалдан авалт</div>
        <div className="mt-2 flex items-end justify-between gap-4 flex-wrap">
          <h1
            className="font-semibold tracking-[-0.025em] leading-none text-white"
            style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(32px, 5vw, 48px)' }}
          >
            Таны сагс
          </h1>
          <div className="text-[13px] tabular-nums" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {cartCount} бараа
          </div>
        </div>
      </div>

      <div className="cont pb-16">
        <div className="grid gap-8 grid-cols-1 lg:grid-cols-[1fr_380px] items-start">
          <div className="min-w-0">
            <div
              className="rounded-2xl px-5 sm:px-6"
              style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              {products.map((product: any, index: number) => (
                <CartCard
                  key={`${product.isRewardItem ? 'reward-' : ''}${product.id}-${product.variant?.id}-${index}`}
                  product={product}
                />
              ))}
            </div>

            <div className="mt-6">
              <RewardSelector />
            </div>

            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
              <a
                href={navigateUrl('/product', pathname)}
                className="inline-flex items-center gap-2 text-[13px] font-medium transition-opacity hover:opacity-80"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Үргэлжлүүлэн худалдан авах
              </a>
              <button
                type="button"
                onClick={clearCart}
                disabled={loading}
                className="inline-flex items-center gap-2 text-[12.5px] font-medium cursor-pointer transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: '#fda4af' }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Сагсыг хоослох
              </button>
            </div>
          </div>

          <aside className="lg:sticky lg:top-32 self-start space-y-4">
            {(() => {
              const subPrice = (amount || 0) - (sale || 0)
              const showShipping = freeShippingProgress && !products.every((p: any) => p.isDigital === true)
              const showMinOrder = vendor?.orderMinAmount > 0 && subPrice < vendor.orderMinAmount
              const orderDisc = activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0
                ? (() => {
                  const targets = activeOrderDiscount.targets || []
                  const target = targets.find((t: any) => t.role === 'target' && t.targetId === null)
                    || targets.find((t: any) => t.role === 'target')
                    || targets[0]
                  const label = target?.valueType === 'percent' && target?.valuePercent
                    ? `${target.valuePercent}%-ийн хөнгөлөлт`
                    : target?.valueType === 'amount' && target?.valueAmount
                      ? `${formatPrice(target.valueAmount)} хөнгөлөлт`
                      : activeOrderDiscount.valueType === 'percent' && activeOrderDiscount.valuePercent
                        ? `${activeOrderDiscount.valuePercent}%-ийн хөнгөлөлт`
                        : activeOrderDiscount.valueType === 'amount' && activeOrderDiscount.valueAmount
                          ? `${formatPrice(activeOrderDiscount.valueAmount)} хөнгөлөлт`
                          : activeOrderDiscount.name || 'Захиалгын хөнгөлөлт'
                  return { minAmt: activeOrderDiscount.minOrderAmount, label }
                })()
                : null
              if (!showShipping && !showMinOrder && !orderDisc) return null

              return (
                <div
                  className="rounded-xl px-5 py-4 space-y-3"
                  style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  {showShipping && (
                    <ProgressRow
                      label={freeShippingProgress.reached
                        ? <span className="font-medium" style={{ color: '#34d399' }}>Үнэгүй хүргэлт хангагдсан</span>
                        : <span className="text-white">Үнэгүй хүргэлтэд <span className="font-semibold tabular-nums">{formatPrice(freeShippingProgress.remaining)}</span> үлдсэн</span>}
                      pct={freeShippingProgress.percentage}
                      barColor="#34d399"
                    />
                  )}
                  {orderDisc && (
                    <ProgressRow
                      label={subPrice >= orderDisc.minAmt
                        ? <span className="font-medium" style={{ color: '#d8b4fe' }}>{orderDisc.label} хангагдсан{orderDiscountAmount > 0 ? ` · −${formatPrice(orderDiscountAmount)}` : ''}</span>
                        : <span className="text-white">{orderDisc.label}-д <span className="font-semibold tabular-nums">{formatPrice(orderDisc.minAmt - subPrice)}</span> үлдсэн</span>}
                      pct={Math.min((subPrice / orderDisc.minAmt) * 100, 100)}
                      barColor="#a855f7"
                    />
                  )}
                  {showMinOrder && (
                    <ProgressRow
                      label={<span className="text-white">Захиалгын доод дүнд <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount - subPrice)}</span> үлдсэн</span>}
                      pct={Math.min((subPrice / vendor.orderMinAmount) * 100, 100)}
                      barColor="#60a5fa"
                    />
                  )}
                </div>
              )
            })()}

            <div
              className="rounded-2xl p-6"
              style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-4" style={{ color: '#a8a8b3' }}>Хураангуй</div>
              <div className="space-y-2.5 text-[13px]">
                <div className="flex justify-between gap-3">
                  <span style={{ color: 'rgba(255,255,255,0.55)' }}>Үнийн дүн</span>
                  <span className="tabular-nums text-white">{formatPrice(amount)}</span>
                </div>
                {discountBreakdown && discountBreakdown.length > 0 && (
                  discountBreakdown.map((d: any) => (
                    <div key={d.id} className="flex justify-between gap-3">
                      <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }} title={d.name}>{d.name}</span>
                      <span className="tabular-nums shrink-0" style={{ color: '#fda4af' }}>− {formatPrice(d.amount)}</span>
                    </div>
                  ))
                )}
                {saleSavingsRow > 0 && (
                  <div className="flex justify-between gap-3">
                    <span style={{ color: 'rgba(255,255,255,0.55)' }}>Хямдрал</span>
                    <span className="tabular-nums shrink-0" style={{ color: '#fda4af' }}>− {formatPrice(saleSavingsRow)}</span>
                  </div>
                )}
                {validCoupon > 0 && (
                  <div className="flex justify-between gap-3">
                    <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }} title={appliedCoupon?.discount?.name || appliedCoupon?.name}>
                      Купон · {appliedCoupon?.discount?.name || appliedCoupon?.name || appliedCoupon?.discount?.code || 'Хөнгөлөлт'}
                    </span>
                    <span className="tabular-nums shrink-0" style={{ color: '#fda4af' }}>− {formatPrice(validCoupon)}</span>
                  </div>
                )}
                {orderDiscountAmount > 0 && (
                  <div className="flex justify-between gap-3">
                    <span className="truncate" style={{ color: 'rgba(255,255,255,0.55)' }} title={activeOrderDiscount?.name}>
                      {activeOrderDiscount?.name ? `Захиалгын · ${activeOrderDiscount.name}` : 'Захиалгын хөнгөлөлт'}
                    </span>
                    <span className="tabular-nums shrink-0" style={{ color: '#d8b4fe' }}>− {formatPrice(orderDiscountAmount)}</span>
                  </div>
                )}
                {loyaltyDiscount > 0 && (
                  <div className="flex justify-between gap-3">
                    <span className="inline-flex items-center gap-1.5" style={{ color: '#a78bfa' }}>
                      <Medal className="w-3.5 h-3.5 shrink-0" /> Лоялти хөнгөлөлт
                    </span>
                    <span className="tabular-nums shrink-0" style={{ color: '#a78bfa' }}>− {formatPrice(loyaltyDiscount + loyaltySaleOffsetAmount)}</span>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 flex items-baseline justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <span className="text-[13px] text-white">Нийт төлбөр</span>
                <span className="text-[20px] font-semibold tabular-nums text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                  {formatPrice(subtotal)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => { window.location.href = checkoutHref }}
                disabled={isValidating || !canCheckout()}
                className="mt-5 w-full h-14 rounded-full text-[14px] font-semibold inline-flex items-center justify-center gap-2 cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'white', color: '#0a0a0c' }}
              >
                {isValidating ? 'Шалгаж байна...' : 'Захиалга баталгаажуулах'} <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <div className="mt-3 text-[11px] text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Төлбөр төлөх үед хүргэлтийн төлбөр тооцогдоно.
              </div>
            </div>

            {hasOutOfStock && (
              <div
                className="rounded-xl p-4 flex items-start gap-3"
                style={{ background: 'rgba(244,63,94,0.06)', border: '1px solid rgba(244,63,94,0.2)' }}
              >
                <PackageX className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#fda4af' }} />
                <div className="flex-1">
                  <div className="text-[13px] font-medium" style={{ color: '#fda4af' }}>Үлдэгдэл дууссан бараа</div>
                  <div className="mt-1 text-[12px]" style={{ color: 'rgba(253,164,175,0.7)' }}>
                    Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна. Устгаад захиалга өгнө үү.
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {(() => {
        const showWishlist = isCustomerAuthActive(vendor) && isLoggedIn && wishlist?.products?.length > 0
        const showRecs = !showWishlist && (isLoadingRecommendations || recommendedProducts.length > 0)
        if (!showWishlist && !showRecs) return null
        return (
          <div className="cont pb-20" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="pt-8 flex items-end justify-between gap-4 flex-wrap mb-7">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: '#a8a8b3' }}>
                  {showWishlist ? 'Таны хүсэл' : 'Таны хувьд'}
                </div>
                <h2
                  className="text-[22px] sm:text-[28px] font-semibold tracking-[-0.02em] leading-none text-white"
                  style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}
                >
                  {showWishlist ? 'Хүслийн жагсаалт' : 'Танд таарах бусад бүтээгдэхүүн'}
                </h2>
              </div>
              <a
                href={navigateUrl('/product', pathname)}
                className="text-[13px] font-medium inline-flex items-center gap-1.5 px-4 h-9 rounded-full transition-opacity hover:opacity-90"
                style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.13)' }}
              >
                Бүгдийг харах <ArrowRight className="h-3 w-3" />
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-10">
              {showWishlist ? (
                wishlist.products.map((product: any, i: number) => (
                  <ProductCard
                    key={`${product.id}-${product.variant?.id || `wishlist-${i}`}`}
                    product={product}
                    vendor={vendor}
                    showVariantName
                  />
                ))
              ) : isLoadingRecommendations ? (
                [...Array(5)].map((_, i) => (
                  <div key={i} className="aspect-4/5 rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
                ))
              ) : (
                recommendedProducts.map((product: any) => (
                  <ProductCard key={product.customId} product={product} vendor={vendor} />
                ))
              )}
            </div>
          </div>
        )
      })()}
    </div>
  )
}
